import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Eye, EyeOff, Mail, Smartphone } from 'lucide-react';
import AuthPremiumShell from '../components/AuthPremiumShell';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function normalizePhone(value) {
  return (value || '').toString().replace(/\D/g, '').slice(-10);
}

function isEmailValue(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').toString().trim());
}

function isPhoneValue(value) {
  return /^[6-9]\d{9}$/.test(normalizePhone(value));
}

function buildIdentifierPayload(identifier) {
  const trimmed = (identifier || '').toString().trim();
  if (isEmailValue(trimmed)) {
    return { email: trimmed.toLowerCase() };
  }
  if (isPhoneValue(trimmed)) {
    return { phone: normalizePhone(trimmed) };
  }
  return null;
}

function formatApiError(result, status, t) {
  const msg = result?.error || result?.message || '';
  if (status === 404 && msg.includes('Registered account')) {
    return t('loginFailedGeneric');
  }
  if (status === 429 || msg.includes('5 minutes')) {
    return t('forgotPasswordFailed');
  }
  if (msg.includes('Passwords match nahi')) {
    return t('passwordsMustMatch');
  }
  if (msg.includes('Password weak ahe')) {
    return t('resetPasswordMinLength');
  }
  if (msg.includes('OTP expire zhala')) {
    return t('forgotPasswordInvalidOtp');
  }
  if (msg.includes('Jast chukha attempts')) {
    return t('forgotPasswordInvalidOtp');
  }
  if (msg.includes('OTP chukha ahe')) {
    return t('forgotPasswordInvalidOtp');
  }
  return t('forgotPasswordFailed');
}

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const identifierLabel = useMemo(
    () => `${t('mobileNumber')} / ${t('emailAddressLabel')}`,
    [t],
  );

  const subtitle = useMemo(() => {
    if (step === 1) return t('forgotPasswordPhoneHint');
    if (step === 2) return t('resetPasswordIntro');
    return t('resetPasswordSuccess');
  }, [step, t]);

  const sendOtp = async () => {
    setError('');
    const payload = buildIdentifierPayload(identifier);
    if (!payload) {
      setError(isEmailValue(identifier) ? t('forgotPasswordInvalidEmail') : t('forgotPasswordInvalidPhone'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password-email-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(formatApiError(result, response.status, t));
        return;
      }
      setStep(2);
    } catch (_err) {
      setError(t('forgotPasswordFailed'));
    } finally {
      setLoading(false);
    }
  };

  const verifyAndReset = async () => {
    setError('');
    if (!/^\d{6}$/.test(otp.trim())) {
      setError(t('forgotPasswordInvalidOtp'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('passwordsMustMatch'));
      return;
    }
    if (newPassword.length < 8 || !/[0-9]/.test(newPassword) || !/[a-zA-Z]/.test(newPassword)) {
      setError(t('resetPasswordMinLength'));
      return;
    }

    const idPayload = buildIdentifierPayload(identifier);
    if (!idPayload) {
      setError(isEmailValue(identifier) ? t('forgotPasswordInvalidEmail') : t('forgotPasswordInvalidPhone'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-reset-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...idPayload,
          otp: otp.trim(),
          newPassword,
          confirmPassword,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(formatApiError(result, response.status, t));
        return;
      }
      setStep(3);
    } catch (_err) {
      setError(t('resetPasswordFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPremiumShell
      title={t('forgotPasswordTitle')}
      subtitle={subtitle}
    >
      {step === 1 && (
        <div className="space-y-1.5">
          <Label htmlFor="forgot-identifier" className="normal-case tracking-normal">
            {identifierLabel}
          </Label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center text-de-gold/70">
              {isEmailValue(identifier) ? (
                <Mail className="h-4 w-4" strokeWidth={1.75} />
              ) : (
                <Smartphone className="h-4 w-4" strokeWidth={1.75} />
              )}
            </span>
            <Input
              id="forgot-identifier"
              className="login-field-input login-field-input--ultra h-11 rounded-xl pl-11"
              type="text"
              inputMode="email"
              placeholder="9876543210"
              value={identifier}
              onChange={(e) => { setIdentifier(e.target.value); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && sendOtp()}
              autoComplete="username"
            />
          </div>
          <p className="text-[11px] leading-snug text-de-muted">{t('forgotPasswordPhoneHint')}</p>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <div className="flex items-start gap-2.5 rounded-xl border border-de-gold/25 bg-de-gold/8 px-3 py-2.5">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-de-gold" strokeWidth={1.75} />
            <p className="text-sm leading-relaxed text-de-muted">
              {t('forgotPasswordSuccess')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="forgot-otp" className="normal-case tracking-normal">{t('otpCode')}</Label>
            <Input
              id="forgot-otp"
              className="login-field-input login-field-input--ultra h-11 rounded-xl"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
              value={otp}
              onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="forgot-new-password" className="normal-case tracking-normal">{t('newPassword')}</Label>
            <div className="relative">
              <Input
                id="forgot-new-password"
                className="login-field-input login-field-input--ultra h-11 rounded-xl pr-11"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-de-gold/80 transition-colors hover:bg-de-gold/10 hover:text-de-gold"
                onClick={() => setShowNewPassword((p) => !p)}
                aria-label={showNewPassword ? t('hide') : t('show')}
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="forgot-confirm-password" className="normal-case tracking-normal">{t('confirmPassword')}</Label>
            <div className="relative">
              <Input
                id="forgot-confirm-password"
                className="login-field-input login-field-input--ultra h-11 rounded-xl pr-11"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && verifyAndReset()}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-de-gold/80 transition-colors hover:bg-de-gold/10 hover:text-de-gold"
                onClick={() => setShowConfirmPassword((p) => !p)}
                aria-label={showConfirmPassword ? t('hide') : t('show')}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <button
            type="button"
            className="text-xs font-medium text-de-gold/80 transition-colors hover:text-de-gold"
            onClick={sendOtp}
            disabled={loading}
          >
            {t('forgotPasswordSendAgain')}
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-300">
            <CheckCircle2 className="h-7 w-7" strokeWidth={1.75} />
          </span>
          <p className="text-lg font-semibold text-white">{t('resetPasswordSuccess')}</p>
        </div>
      )}

      {error && (
        <div className="login-error-box mt-4 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300">
          {error}
        </div>
      )}

      {step === 1 && (
        <Button
          type="button"
          className="login-btn-primary login-btn-primary--ultra mt-5 h-12 w-full rounded-2xl border-0 bg-gradient-to-r from-[#a07830] via-[#e2c97e] to-[#a07830] text-sm font-extrabold uppercase tracking-[0.12em] text-[#0a1628] shadow-[0_12px_32px_rgba(201,168,76,0.32)]"
          onClick={sendOtp}
          disabled={loading}
        >
          {loading ? t('forgotPasswordSendingOtp') : t('forgotPasswordSendOtp')}
        </Button>
      )}

      {step === 2 && (
        <Button
          type="button"
          className="login-btn-primary login-btn-primary--ultra mt-5 h-12 w-full rounded-2xl border-0 bg-gradient-to-r from-[#a07830] via-[#e2c97e] to-[#a07830] text-sm font-extrabold uppercase tracking-[0.12em] text-[#0a1628] shadow-[0_12px_32px_rgba(201,168,76,0.32)]"
          onClick={verifyAndReset}
          disabled={loading}
        >
          {loading ? t('resetPasswordUpdating') : t('resetPasswordSubmit')}
        </Button>
      )}

      {step === 3 && (
        <Button
          type="button"
          className="login-btn-primary login-btn-primary--ultra mt-5 h-12 w-full rounded-2xl border-0 bg-gradient-to-r from-[#a07830] via-[#e2c97e] to-[#a07830] text-sm font-extrabold uppercase tracking-[0.12em] text-[#0a1628] shadow-[0_12px_32px_rgba(201,168,76,0.32)]"
          onClick={() => navigate('/login')}
        >
          <span className="inline-flex items-center gap-2">
            {t('signIn')}
            <ArrowRight className="h-4 w-4" />
          </span>
        </Button>
      )}
    </AuthPremiumShell>
  );
};

export default ForgotPassword;
